import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { KpiCard } from "../components/KpiCard";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import { AlertTriangle } from "lucide-react";

export default function RooftopPerformance() {
  const { rooftop_id } = useParams();
  const { user } = useAuth();
  const [scorecard, setScorecard] = useState([]);
  const [rooftop, setRooftop] = useState(null);

  useEffect(() => {
    api.get(`/rooftops/${rooftop_id}`).then((r) => setRooftop(r.data));
    api.get("/corporate/buyer-scorecard").then((r) => setScorecard(r.data.filter((b) => b.rooftop_id === rooftop_id)));
  }, [rooftop_id]);

  const me = scorecard.find((b) => b.buyer_id === user?.user_id);
  const showPersonal = user?.role === "buyer" && me;

  return (
    <Layout variant="rooftop" rooftopId={rooftop_id} rooftopName={rooftop?.name}>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Performance</div>
        <h1 className="text-3xl font-bold text-navy">{showPersonal ? "My scorecard" : "Team performance"}</h1>
      </div>

      {showPersonal && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard label="Units 90d" value={me.units} testId="my-units" />
          <KpiCard label="Avg gross" value={`$${me.avg_gross.toLocaleString()}`} testId="my-gross" />
          <KpiCard label="Hit rate" value={`${me.strong_buy_hit_rate}%`} testId="my-hit-rate" tone={me.strong_buy_hit_rate < 65 ? "alert" : "default"} />
          <KpiCard label="Override rate" value={`${me.override_rate}%`} testId="my-override" tone={me.override_rate > 25 ? "alert" : "default"} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="team-scorecard">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] uppercase tracking-wider text-slate-600">
              <th className="text-left px-4 py-3 font-bold">Buyer</th>
              <th className="text-right px-4 py-3 font-bold">Units</th>
              <th className="text-right px-4 py-3 font-bold">Avg gross</th>
              <th className="text-right px-4 py-3 font-bold">Hit rate</th>
              <th className="text-right px-4 py-3 font-bold">Override</th>
              <th className="text-right px-4 py-3 font-bold">CTM</th>
              <th className="text-center px-4 py-3 font-bold">Flag</th>
            </tr>
          </thead>
          <tbody>
            {scorecard.map((b) => {
              const flagged = b.override_rate > 25 || b.strong_buy_hit_rate < 65;
              return (
                <tr key={b.buyer_id} className={cn("border-b border-slate-100", b.buyer_id === user?.user_id && "bg-freddy-red/5")}>
                  <td className="px-4 py-3 font-semibold text-navy">{b.buyer_name}{b.buyer_id === user?.user_id && <span className="ml-2 text-[10px] text-freddy-red font-bold uppercase">You</span>}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold">{b.units}</td>
                  <td className="px-4 py-3 text-right tabular font-bold">${b.avg_gross.toLocaleString()}</td>
                  <td className={cn("px-4 py-3 text-right tabular font-semibold",
                    b.strong_buy_hit_rate >= 75 ? "text-verdict-strongText" : b.strong_buy_hit_rate >= 65 ? "text-slate-700" : "text-verdict-passText")}>{b.strong_buy_hit_rate}%</td>
                  <td className={cn("px-4 py-3 text-right tabular font-semibold",
                    b.override_rate <= 15 ? "text-verdict-strongText" : b.override_rate <= 25 ? "text-verdict-conditionalText" : "text-verdict-passText")}>{b.override_rate}%</td>
                  <td className="px-4 py-3 text-right tabular">{b.cost_to_market}%</td>
                  <td className="px-4 py-3 text-center">{flagged && <AlertTriangle size={14} className="inline text-freddy-red" />}</td>
                </tr>
              );
            })}
            {scorecard.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">No buyer activity at this rooftop yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
